import { MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline';
import React, {
  ComponentType,
  FC,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { Controlled as ControlledZoom } from 'react-medium-image-zoom';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import styled, { DefaultTheme, StyledComponent } from 'styled-components';
import Swal from 'sweetalert2';
import pluralize from 'pluralize';
import { popItemFromConfig, addToCart } from '../../../actions/v3Order';
import type { Slot as SlotConfig } from '../../../reducers/v3Order/slotState';
import type { ImageIntentContext } from '../../../utils/imageIntentReporter';
import cartUtils from '../../../utils/cart';
import formatUSD from '../../../utils/formatUSD';
import { isJpgImage } from '../../../utils/imageHelpers';
import productConfigUtils from '../../../utils/productConfig';
import { shouldBreakConfigLoop } from '../helpers/config';
import {
  isShippingRestrictedForParent,
  ypxHasHiddenChildren,
} from '../helpers/internationalRestriction';
import { extraConfigProducts, getProductByLocation } from '../helpers/store';
import useScreenSize, { MOBILE_L } from '../hooks/screen';
import useFoldOnSelect from '../hooks/useFoldOnSelect';
import useIsPhone from '../hooks/useIsPhone';
import CategoryPicker from '../shared/CategoryPicker';
import DoneScreenJs from './done';
import {
  ImageProtectionOverlay,
  useImageIntentFriction,
  useProtectedZoomContent,
} from '../shared/protectedImage';
import useImageIntentZoomContent from '../shared/protectedImage/imageIntentZoomContent';
import {
  imageIntentGalleryOf,
  ImageIntentGallery,
} from '../shared/protectedImage/imageIntentGallery';
import type { CategoryPickerCategory } from '../shared/categoryPickerTypes';
import {
  BackgroundNamePriceWrapper,
  CategoryButton as CategoryButtonJs,
  CategoryFoldWrapper,
  CategorySelector,
  ConfigScreenWrapper,
  Instruction,
  ItemImage,
  ItemImageBackgroundSelection,
  ItemImageBackgroundWrapper,
  ItemImageContainer,
  ItemImageProtectionBox,
  ItemPanel,
  ItemsGrid as ItemsGridJs,
  ItemWrapper,
  PoseImage,
  PoseSlotBox,
  PoseSlotBoxFilled,
  PoseSlotsRow as PoseSlotsRowJs,
  ZoomButtonWrapper,
} from './productConfigStyles';
import SlotSelector from './slotSelector';
import RequiredProductForm from '../products/required-product-form/RequiredProductForm.jsx';
import ProductOptionsSelector from '../products/regular/subcomponents/ProductOptionsSelector';
import { setCurrentProduct } from '../../../reducers/productConfigSlice';
import {
  addToConfig as addToConfigJs,
  getPosesForSlot,
  getRequiredPoseCountForSlot,
} from './utils';
import {
  selectBackgroundsSatisfied,
  selectConfigFlowPages,
  selectConfiguredChildProducts,
} from '../../../selectors';
import { productHasGroupRecord } from '../products/imageRecords';
import UnsupportedProductConfig from '../products/UnsupportedProductConfig';

interface ConfigPose {
  id?: number;
  url?: string;
  selected_crop?: { url?: string } | null;
}

interface CroppedImage {
  id: number;
  name: string;
  url: string;
}

// One tile of the wizard's grid: a child product, a pose, or a background.
export interface ConfigItem {
  id: number;
  name: string;
  display_name?: string | null;
  image_thumbnail?: string | null;
  url?: string;
  image_large?: string | null;
  price?: number | null;
  cropped_images?: CroppedImage[] | null;
}

export interface ConfigCategory<T> extends CategoryPickerCategory {
  items: T[];
}

interface ParentProduct {
  name: string;
  price: number;
  maximum_children_count: number;
  required_children_count?: number | null;
  additional_data?: { free_indices?: number[] } & Record<string, unknown>;
}

interface RequiredProductField {
  key: string;
  [field: string]: unknown;
}

interface ShootSlice {
  chargeForYpxBackgroundUpgrades?: boolean | null;
  shoot?: {
    company_auth_token?: string | null;
    background_choice_mode?: string | null;
  } | null;
  shootProducts: unknown[];
  suggestedProducts?: unknown[];
  additionalOfferProducts?: unknown[];
}

// The slices of the store this screen reads.
interface ConfigScreenState {
  shoot: ShootSlice;
  additionalData: {
    studentId?: number | string | null;
    studentPreviewImageUrl?: string | null;
  };
  v3Order: {
    productConfig: Record<string, SlotConfig>;
    orders: unknown;
  };
  parent: unknown;
  productConfig: { selectedProductOptions: Record<string, unknown> };
}

type AppDispatch = ThunkDispatch<unknown, unknown, AnyAction>;

// --- Untyped JavaScript at this file's edges ---------------------------------
// TypeScript infers these modules' types from their JavaScript, which misses
// props a styled component reads only in its interpolations, drops every prop
// of a withRouter-wrapped component, and reads addToConfig's `cropIndex = null`
// default as null-only. Each is typed here to what this file passes, as
// Background.tsx types this screen at its own edge; nothing changes at runtime.

interface GridColumns {
  itemsDesktop: number;
  itemsMobile: number;
}

const ItemsGrid = ItemsGridJs as StyledComponent<
  'div',
  DefaultTheme,
  GridColumns
>;
const PoseSlotsRow = PoseSlotsRowJs as StyledComponent<
  'div',
  DefaultTheme,
  GridColumns
>;
const CategoryButton = CategoryButtonJs as StyledComponent<
  'button',
  DefaultTheme,
  { selected: boolean }
>;

// done.js's DoneScreen takes no props today (it reads the store), so the
// product and onDone passed below reach nothing; the package is submitted from
// SlotSelector. Typed to the call as it stands rather than changed here.
const DoneScreen = DoneScreenJs as unknown as ComponentType<{
  product: ParentProduct;
  onDone: () => void;
}>;

interface AddToConfigArgs {
  dispatch: AppDispatch;
  history: ReturnType<typeof useHistory>;
  productConfig: Record<string, SlotConfig>;
  pages: string[];
  itemToAdd: ConfigItem;
  event: ShootSlice;
  cropIndex?: number | null;
}

const addToConfig = addToConfigJs as unknown as (args: AddToConfigArgs) => void;

const DoneWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: min-content;
  min-width: 260px;
`;

// Palette matches the existing BannerMessage `alert` style — cream
// (#fcf8e3 / #faf2cc / #8a6d3b) so the contextual notice reads as part
// of the same warning family as the shoot banner at the top of the page.
const HiddenChildrenNotice = styled.div`
  background: #fcf8e3;
  border: 1px solid #faf2cc;
  color: #8a6d3b;
  border-radius: 4px;
  padding: 8px 12px;
  margin: 0 0 12px;
  font-size: 12px;
  line-height: 1.4;

  // ItemPanel has margin-top: 25px on desktop; pull the notice up by the
  // same amount so its top edge aligns with the SlotSelector title on the
  // left column instead of floating mid-row.
  @media (min-width: ${MOBILE_L}px) {
    margin-top: -25px;
  }
`;

// The pose layered over the backgrounds: the last pose of the latest slot.
const getLastPose = (
  productConfig: Record<string, SlotConfig>,
): ConfigPose | undefined => {
  const lastKey = Object.keys(productConfig).sort(
    (a, b) => Number(b) - Number(a),
  )[0];
  const config = productConfig[lastKey];
  const poses = config?.poses ?? (config?.pose ? [config.pose] : []);
  return Array.isArray(poses) ? poses[poses.length - 1] : config?.pose;
};

const poseUrlOf = (pose: ConfigPose | null | undefined): string | null =>
  (pose?.selected_crop ? pose.selected_crop?.url : pose?.url) || null;

interface ItemProps {
  src?: string;
  largeSrc?: string | null;
  onClick?: () => void;
  name: string;
  price?: number | null;
  intentGallery?: ImageIntentGallery | Record<string, never>;
}

const Item: FC<ItemProps> = ({
  src,
  largeSrc,
  onClick = () => {},
  name,
  price,
  intentGallery = {},
}) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const { pathname } = useLocation();
  const { productConfig } = useSelector(
    (state: ConfigScreenState) => state.v3Order,
  );
  const event = useSelector((state: ConfigScreenState) => state.shoot);
  const lastPose = getLastPose(productConfig);
  const poseUrl = poseUrlOf(lastPose);
  const { studentPreviewImageUrl, studentId } = useSelector(
    (state: ConfigScreenState) => state.additionalData,
  );
  const orders = useSelector(
    (state: ConfigScreenState) => state.v3Order.orders,
  );
  const { getFrictionProps } = useImageIntentFriction();

  let isBackgroundSelection = false;
  // This grid is shared by every config step. Only the pose step lists the
  // child's photographs (`item.url` / crop urls); the product step lists
  // catalogue artwork (`item.image_thumbnail`) and must stay unprotected.
  const isPoseSelection = /\/pose\//.test(pathname || '');

  const handleZoomChange = useCallback((shouldZoom: boolean) => {
    setIsZoomed(shouldZoom);
  }, []);

  const handleZoomClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsZoomed((zoomed) => !zoomed);
  };

  if (pathname && pathname.includes('background')) {
    isBackgroundSelection = true;
  }

  let chargeForYpxBackgroundUpgrades;

  if (typeof event.chargeForYpxBackgroundUpgrades === 'boolean') {
    chargeForYpxBackgroundUpgrades = event.chargeForYpxBackgroundUpgrades;
  } else {
    chargeForYpxBackgroundUpgrades = true;
  }

  const orderContainsSingleBackgroundUpgrade =
    cartUtils.orderContainsSingleBackgroundUpgrade(orders, studentId);

  const productConfigContainsSingleBackgroundUpgrade =
    productConfigUtils.productConfigContainsSingleBackgroundUpgrade(
      productConfig,
    );

  const composedPoseUrl = poseUrl || studentPreviewImageUrl;
  // Only the composite (pose layered over the background) shows the child. A
  // bare background swatch — no pose, or a JPG pose that cannot be layered —
  // is catalogue artwork and gets neither the vignette nor save telemetry.
  const showsComposedPose = !!composedPoseUrl && !isJpgImage(composedPoseUrl);
  // The gallery comes from the hook's Redux defaults (the order's gallery),
  // except for a photo pooled from another gallery (a sibling's, or none for
  // a group photo), which carries its own: the tile's pose (intentGallery)
  // or, on the background step, the pose layered over the background.
  const imageContext: ImageIntentContext = {
    surface: 'product_config',
    // `src` is the background thumbnail here and the pose url on the pose
    // step — unique per tile either way, so tiles get their own throttle
    // bucket instead of sharing one.
    imageKey: src,
    ...(isBackgroundSelection ? imageIntentGalleryOf(lastPose) : intentGallery),
  };
  // The zoomed image renders in a portal outside this tile. On the pose step
  // it is the child's photograph and gets its own friction props (surface
  // `product_config_zoom`) and, when the studio has protection on, its own
  // overlay; elsewhere it is catalogue artwork.
  const intentZoomContent = useImageIntentZoomContent(imageContext);
  const poseZoomContent = useProtectedZoomContent(intentZoomContent);
  // On the background step rmiz zooms the first <img> — the bare background —
  // and React bubbles the portal's events through ControlledZoom to the tile
  // wrapper. So the friction props go on the two images inside the zoom, not
  // on the wrapper, which only blocks the right-click as before.
  const compositeProps = showsComposedPose
    ? getFrictionProps(imageContext)
    : { onContextMenu: (event: React.MouseEvent) => event.preventDefault() };

  return (
    <ItemWrapper>
      {isBackgroundSelection && (
        <>
          <ItemImageBackgroundWrapper
            onContextMenu={(event) => event.preventDefault()}
          >
            <ControlledZoom isZoomed={isZoomed} onZoomChange={handleZoomChange}>
              <ItemImageBackgroundSelection
                src={src}
                alt={name}
                onClick={onClick}
                {...compositeProps}
              />
              {/* Don't layer JPG images - they don't have alpha transparency */}
              {showsComposedPose && (
                <PoseImage
                  src={composedPoseUrl}
                  alt="Pose Image"
                  onClick={onClick}
                  {...compositeProps}
                />
              )}
            </ControlledZoom>
            {/* ItemImageBackgroundWrapper is fixed to the exact 8x10 box the
                background and the pose are both sized to, so the overlay lands
                on the image and nothing else. */}
            {showsComposedPose && (
              <ImageProtectionOverlay
                context={imageContext}
                imageSrc={composedPoseUrl}
              />
            )}
            <ZoomButtonWrapper onClick={handleZoomClick}>
              <MagnifyingGlassPlusIcon />
            </ZoomButtonWrapper>
          </ItemImageBackgroundWrapper>
          <BackgroundNamePriceWrapper>
            <p>
              {name}
              {price &&
                !orderContainsSingleBackgroundUpgrade &&
                !productConfigContainsSingleBackgroundUpgrade &&
                chargeForYpxBackgroundUpgrades && (
                  <>
                    <br />
                    {formatUSD(price)}
                  </>
                )}
            </p>
          </BackgroundNamePriceWrapper>
        </>
      )}
      {!isBackgroundSelection && (
        <>
          <ItemImageContainer onContextMenu={(event) => event.preventDefault()}>
            <ControlledZoom
              isZoomed={isZoomed}
              onZoomChange={handleZoomChange}
              // rmiz reads only a truthy src, so null and undefined match.
              zoomImg={{ src: largeSrc ?? undefined }}
              // rmiz clones the image into a portalled <dialog>, so neither the
              // tile's handlers nor its sibling vignette travel with it. On the
              // pose step `largeSrc` is null (poses carry no `image_large`), so
              // rmiz zooms this very image — the child — and the modal needs
              // its own friction props and overlay.
              ZoomContent={isPoseSelection ? poseZoomContent : undefined}
            >
              {isPoseSelection ? (
                <ItemImageProtectionBox {...getFrictionProps(imageContext)}>
                  <ItemImage src={src} onClick={onClick} alt={name} />
                  <ImageProtectionOverlay
                    context={imageContext}
                    imageSrc={src}
                  />
                </ItemImageProtectionBox>
              ) : (
                <ItemImage
                  src={src}
                  onClick={onClick}
                  alt={name}
                  onContextMenu={(event) => event.preventDefault()}
                />
              )}
            </ControlledZoom>
            <ZoomButtonWrapper onClick={handleZoomClick}>
              <MagnifyingGlassPlusIcon />
            </ZoomButtonWrapper>

            {event?.shoot?.company_auth_token === 'wagner' ? (
              <p>{name.length > 55 ? name.substring(0, 55) + '...' : name}</p>
            ) : (
              <p>{name.length > 24 ? name.substring(0, 24) + '...' : name}</p>
            )}
          </ItemImageContainer>
        </>
      )}
    </ItemWrapper>
  );
};

interface ItemButtonProps
  extends Omit<ItemProps, 'onClick' | 'price' | 'intentGallery'> {
  item: ConfigItem;
  cropIndex?: number | null;
  id?: number;
  shoot?: ShootSlice;
}

const ItemButton: FC<ItemButtonProps> = ({
  item,
  cropIndex = null,
  ...props
}) => {
  const history = useHistory();
  // Selected here rather than threaded down: ItemButton is the only consumer on
  // this branch, and the prop had to be forwarded through ItemGrid and
  // CategoryGrid to reach it.
  const pages = useSelector(selectConfigFlowPages);
  const { productConfig } = useSelector((s: ConfigScreenState) => s.v3Order);
  const event = useSelector((s: ConfigScreenState) => s.shoot);
  const dispatch = useDispatch<AppDispatch>();

  const handleClick = () => {
    addToConfig({
      dispatch,
      history,
      productConfig,
      pages,
      itemToAdd: item,
      event,
      cropIndex,
    });
  };

  return (
    <Item
      price={item.price || null}
      intentGallery={imageIntentGalleryOf(item)}
      {...props}
      onClick={handleClick}
    />
  );
};

// ENG-2551. Module scope on purpose: this was previously declared inside
// ConfigScreen's body, giving it a new identity every parent render — React
// remounted it each time and silently reset the selected category. Exported
// for direct unit testing (categoryGrid.test.tsx).
interface CategoryGridProps<T> {
  categories?: ConfigCategory<T>[];
  renderItems: (items: T[]) => React.ReactNode;
}

// Generic over the tile type: the grid only hands each category's items back
// to renderItems.
export const CategoryGrid = <T,>({
  categories = [],
  renderItems,
}: CategoryGridProps<T>): React.ReactElement => {
  // Track the selection by index: names are studio-authored and not unique
  // (two sets can both be "Studio"), and Background.tsx rebuilds these category
  // objects from shoot data on every render, so neither the name nor the
  // object identity distinguishes one set from another.
  const [selectedIndex, setSelectedIndex] = useState(0);
  const isPhone = useIsPhone();

  // One set is not a choice: nothing to collapse, and no picker once
  // collapsed. On phones the lone button goes too, so the customer lands on
  // the thumbnails; >=768px still renders it exactly as it always has.
  const hasChoice = categories.length > 1;
  const showSelector = hasChoice || !isPhone;

  // Phones land folded: the picker opens collapsed on the first set, and a
  // selection re-folds and scrolls that set's first row into view.
  const { folded, fold, unfold, gridRef } = useFoldOnSelect({
    foldByDefault: hasChoice,
  });

  const selectCategory = (_category: CategoryPickerCategory, index: number) => {
    setSelectedIndex(index);
    if (hasChoice) fold();
  };

  // An index left over from a longer category list falls back to the first.
  const visibleCategory = categories[selectedIndex] ?? categories[0];

  // A shoot with no background categories has nothing to pick from. `[]` is
  // truthy at the call site, so without this we would dereference an absent
  // category and crash the step.
  if (!visibleCategory) {
    return (
      <CategoryFoldWrapper ref={gridRef}>{renderItems([])}</CategoryFoldWrapper>
    );
  }

  return (
    <CategoryFoldWrapper ref={gridRef}>
      {folded ? (
        <CategoryPicker
          categories={categories}
          selectedIndex={selectedIndex}
          onSelect={selectCategory}
          onExpand={unfold}
        />
      ) : !showSelector ? null : (
        <CategorySelector>
          {categories.map((c, index) => (
            <CategoryButton
              key={c.id ?? index}
              selected={index === selectedIndex}
              onClick={() => selectCategory(c, index)}
            >
              {c.name}
            </CategoryButton>
          ))}
        </CategorySelector>
      )}
      {renderItems(visibleCategory.items)}
    </CategoryFoldWrapper>
  );
};

interface ConfigScreenProps {
  // Every step but the background one lists its tiles here; that step passes
  // categories instead (see Background.tsx).
  items: ConfigItem[];
  categories?: ConfigCategory<ConfigItem>[];
  itemsDesktop: number;
  itemsMobile: number;
  instruction: string;
  gridStyle?: React.CSSProperties;
  children?: React.ReactNode;
}

export const ConfigScreen: FC<ConfigScreenProps> = ({
  items,
  categories,
  itemsDesktop,
  itemsMobile,
  instruction,
  gridStyle = {},
  children,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const history = useHistory();
  const shoot = useSelector((s: ConfigScreenState) => s.shoot);
  const additionalData = useSelector(
    (s: ConfigScreenState) => s.additionalData,
  );
  // hooks/screen.js is untyped JavaScript; it yields 'Mobile', 'Desktop', or
  // null before the first measurement, and whether it is still measuring.
  const [screenSize, loadingScreenSize] = useScreenSize() as [
    string | null,
    boolean,
  ];
  const hash = location.hash?.replace('#', '');
  const product: ParentProduct = getProductByLocation(
    shoot?.shootProducts,
    location,
    extraConfigProducts(shoot),
  );
  const parent = useSelector((s: ConfigScreenState) => s.parent);
  const restricted = isShippingRestrictedForParent(shoot?.shoot, parent);
  const showHiddenNotice = ypxHasHiddenChildren(product, restricted);
  const { getFrictionProps } = useImageIntentFriction({
    surface: 'product_config',
  });

  const {
    additional_data = {},
    additional_data: { free_indices = [] } = {},
    required_children_count,
  } = product;

  const isYouPickX = Object.keys(additional_data).length == 0;
  const isBYO = Object.keys(additional_data).length > 0;

  const { name, price, maximum_children_count: slotCount } = product;
  const hasHash = !!hash;
  const pages = useSelector(selectConfigFlowPages);
  const { productConfig } = useSelector((s: ConfigScreenState) => s.v3Order);
  const { control, handleSubmit, register, setValue } = useForm();
  const [requiredProductFields, setRequiredProductFields] = useState<
    RequiredProductField[]
  >([]);
  const selectedProductOptions = useSelector(
    (s: ConfigScreenState) => s.productConfig.selectedProductOptions,
  );

  useEffect(() => {
    const currentUrlSplit = history.location.pathname.split('/');
    const currentSection = currentUrlSplit[currentUrlSplit.length - 2];
    const currentIndex = parseInt(
      currentUrlSplit[currentUrlSplit.length - 1]?.replace('#slots', ''),
    );
    const item = productConfig[currentIndex];
    const requiredFieldsFromAllProducts: RequiredProductField[][] = [];
    Object.keys(productConfig).forEach((key, index) => {
      if (productConfig[key].product?.required_product_fields?.length > 0) {
        requiredFieldsFromAllProducts.push(
          productConfig[key].product?.required_product_fields,
        );
      }
    });

    const allFields = requiredFieldsFromAllProducts.flat();
    const uniqueFields = allFields.filter(
      (field, index) =>
        allFields.findIndex((f) => f.key === field.key) === index,
    );
    setRequiredProductFields(uniqueFields);
    if (item && item[currentSection])
      dispatch(popItemFromConfig(currentIndex, currentSection));
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  // Keep the Redux currentProduct in sync with the product derived from the
  // URL so ProductOptionsSelector (which reads currentProduct) renders the
  // option sets for the product actually being configured. setCurrentProduct
  // also seeds/reconciles the default option selections.
  useEffect(() => {
    dispatch(setCurrentProduct(product));
  }, [product, dispatch]);

  const shouldShowSlots = screenSize !== 'Mobile' || hasHash;
  const shouldShowItems = screenSize !== 'Mobile' || !hasHash;

  // For pose screen with multiple poses: current slot index and selected poses
  const pathParts = location.pathname.split('/').filter(Boolean);
  const currentSection = pathParts[pathParts.length - 2];
  const currentSlotIndex = parseInt(
    pathParts[pathParts.length - 1]?.replace('#slots', '') || '0',
    10,
  );
  const isPoseScreen = currentSection === 'pose';
  const currentSlotConfig = productConfig[currentSlotIndex];
  // This wizard's pose step only knows the individual gallery — it has no
  // group-image source, so a child with a group pose slot (pure group or a
  // multiple-image child with a group node) would silently offer the wrong
  // photos. Guard with an explanation instead.
  const slotProductUnsupported =
    isPoseScreen && productHasGroupRecord(currentSlotConfig?.product);
  const posesCountForSlot =
    typeof currentSlotConfig?.product?.poses_count === 'number'
      ? currentSlotConfig.product.poses_count
      : 1;
  const selectedPosesForSlot: ConfigPose[] = isPoseScreen
    ? getPosesForSlot(currentSlotConfig)
    : [];

  // On pose screen with multiple poses per slot, hide poses already selected for this slot
  const selectedPoseIds = (selectedPosesForSlot || [])
    .map((p) => p?.id)
    .filter(Boolean);
  const itemsToShow =
    isPoseScreen && selectedPoseIds.length > 0
      ? items.filter((item) => !selectedPoseIds.includes(item.id))
      : items;

  const products = useSelector(selectConfiguredChildProducts);

  const background_choice_mode = useSelector(
    (state: ConfigScreenState) => state.shoot?.shoot?.background_choice_mode,
  );
  const isGreenScreenEvent =
    background_choice_mode === 'advanced_greenscreen_backgrounds_only';

  // Never below 1: required_children_count is an integer on every parent row
  // and defaults to 0, so taking it verbatim made this 0 for any package left
  // at the default. That enabled Add to Cart with nothing chosen and let
  // submitOrder's guard through, which is how an empty package reached the
  // cart in the first place (ENG-1683). A package always needs at least one
  // item; addToCart refuses zero regardless, so leaving the button live only
  // traded a disabled button for a dead-end alert.
  const minimumChildCount = Math.max(
    1,
    isYouPickX &&
      typeof required_children_count === 'number' &&
      Number.isInteger(required_children_count)
      ? required_children_count
      : isYouPickX
      ? slotCount
      : 1,
  );

  // Per-slot pose requirement: each slot with a product must have its required number of poses (0, 1, or 2)
  const allPosesSatisfied = Object.values(productConfig).every((config) => {
    const required = getRequiredPoseCountForSlot(config, pages);
    return getPosesForSlot(config).length >= required;
  });
  // Asked per slot, not as a count: a background belonging to a child that cannot
  // use one must never satisfy the requirement of a child that still owes one.
  // See selectBackgroundsSatisfied.
  const allBackgroundsSatisfied = useSelector(selectBackgroundsSatisfied);

  const actionButtonEnabled =
    allPosesSatisfied &&
    allBackgroundsSatisfied &&
    products.length >= minimumChildCount;

  const submitOrder = () => {
    handleSubmit((formState) => {
      // TODO: handle poses

      if (isBYO) {
        if ([...products].length < minimumChildCount)
          return Swal.fire(
            `Please configure at least ${minimumChildCount} item(s).`,
          );

        if (!allBackgroundsSatisfied && isGreenScreenEvent)
          return Swal.fire(`Please finish configuring all items.`);
      }

      if (isYouPickX && !actionButtonEnabled) {
        return Swal.fire(
          `Please configure ${minimumChildCount} ${pluralize(
            'item',
            minimumChildCount,
          )}.`,
        );
      }

      dispatch(
        addToCart({
          studentId: additionalData.studentId,
          fullproductData: product,
          // A package's poses and backgrounds travel in productConfig, not
          // here. addToCart is untyped JavaScript, so its destructured keys
          // without defaults read as required; naming them undefined is what
          // leaving them out already passed.
          backgroundIds: undefined,
          backgroundSelections: undefined,
          poseIds: undefined,
          poses: undefined,
          legacyRequiredFields: undefined,
          productOptionKey: undefined,
          requiredFields: formState,
          productOptions: selectedProductOptions,
        }),
      );
    })();
  };

  if (loadingScreenSize) return <div />;

  const ItemGrid: FC<{
    itemsDesktop: number;
    itemsMobile: number;
    items: ConfigItem[];
    gridStyle?: React.CSSProperties;
  }> = ({ itemsDesktop, itemsMobile, items, gridStyle = {} }) => (
    <ItemsGrid
      itemsDesktop={itemsDesktop}
      itemsMobile={itemsMobile}
      style={gridStyle}
    >
      {items.map((item) => {
        const croppedImages = item.cropped_images;
        if (croppedImages && croppedImages.length > 0) {
          return croppedImages.map((image, i) => (
            <ItemButton
              key={`cropped-img-${image.id}`}
              id={item.id}
              name={image.name}
              src={image.url}
              shoot={shoot}
              item={item}
              cropIndex={i}
            />
          ));
        }
        return (
          <ItemButton
            key={item.id}
            id={item.id}
            name={item.display_name || item.name}
            src={item.image_thumbnail || item.url}
            largeSrc={item.image_large || null}
            shoot={shoot}
            item={item}
          />
        );
      })}
    </ItemsGrid>
  );

  return (
    <>
      <ConfigScreenWrapper>
        {shouldShowSlots && (
          <SlotSelector instruction={instruction} handleSubmit={handleSubmit} />
        )}
        {shouldBreakConfigLoop(
          shoot.shootProducts,
          location,
          extraConfigProducts(shoot),
        ) && shouldShowItems ? (
          <></>
        ) : shouldShowItems ? (
          <ItemPanel>
            {screenSize !== 'Desktop' && (
              <h3>
                {name} <br />
                <Instruction>{instruction}</Instruction>
              </h3>
            )}
            {showHiddenNotice && (
              <HiddenChildrenNotice role="status">
                Some items in this bundle aren't available for shipping outside
                the US and have been hidden.
              </HiddenChildrenNotice>
            )}
            {slotProductUnsupported ? (
              <UnsupportedProductConfig
                productName={currentSlotConfig?.product?.name}
              />
            ) : children ? (
              children
            ) : categories ? (
              <CategoryGrid
                categories={categories}
                renderItems={(categoryItems) => (
                  <ItemGrid
                    itemsDesktop={itemsDesktop}
                    itemsMobile={itemsMobile}
                    gridStyle={gridStyle}
                    items={categoryItems}
                  />
                )}
              />
            ) : (
              <>
                {isPoseScreen && posesCountForSlot > 1 && (
                  <PoseSlotsRow
                    itemsDesktop={itemsDesktop}
                    itemsMobile={itemsMobile}
                  >
                    {Array.from({ length: posesCountForSlot }).map((_, i) => {
                      if (!selectedPosesForSlot[i]) {
                        return <PoseSlotBox key={i}>Pose {i + 1}</PoseSlotBox>;
                      }

                      const slotPoseUrl =
                        selectedPosesForSlot[i].selected_crop?.url ||
                        selectedPosesForSlot[i].url;
                      const slotContext: ImageIntentContext = {
                        surface: 'product_config',
                        imageKey: slotPoseUrl || `pose-slot-${i}`,
                        ...imageIntentGalleryOf(selectedPosesForSlot[i]),
                      };

                      return (
                        // PoseSlotBoxFilled is a fixed 3/4 box whose <img> is
                        // `width/height: 100%; object-fit: cover` — the overlay
                        // box and the image box are the same rectangle.
                        <PoseSlotBoxFilled key={i}>
                          <img
                            src={slotPoseUrl}
                            alt={`Pose ${i + 1}`}
                            {...getFrictionProps(slotContext)}
                          />
                          <ImageProtectionOverlay
                            context={slotContext}
                            imageSrc={slotPoseUrl}
                          />
                        </PoseSlotBoxFilled>
                      );
                    })}
                  </PoseSlotsRow>
                )}
                <ItemGrid
                  itemsDesktop={itemsDesktop}
                  itemsMobile={itemsMobile}
                  gridStyle={gridStyle}
                  items={itemsToShow}
                />
              </>
            )}
          </ItemPanel>
        ) : (
          <div />
        )}
        {shouldBreakConfigLoop(
          shoot.shootProducts,
          location,
          extraConfigProducts(shoot),
        ) &&
          (requiredProductFields.length > 0 ? (
            <DoneWrapper>
              <RequiredProductForm
                register={register}
                control={control}
                setValue={setValue}
              />
              <ProductOptionsSelector />
              <DoneScreen product={product} onDone={submitOrder} />
            </DoneWrapper>
          ) : (
            <DoneWrapper>
              <ProductOptionsSelector />
              <DoneScreen product={product} onDone={submitOrder} />
            </DoneWrapper>
          ))}
      </ConfigScreenWrapper>
    </>
  );
};

export default ConfigScreen;
